import { Data } from 'common';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ReactToPrint from 'react-to-print';

const { Carrier } = Data;

const usCurrencyFormatter = new Intl.NumberFormat('en-US', {
	style: 'currency',
	currency: 'USD',
});

class PrintReceipt extends Component {
	constructor(props) {
		super(props);
		this.pritReceiptRef = null;
		this.reactToPrintButton = React.createRef();
	}

	componentDidMount() {
		this.reactToPrintButton.current.click();
	}

	formatDate = () => {
		const date = new Date();
		return `${date.toDateString()}`;
	};

	buildAddressLines = address => {
		if (isObject(address)) {
			const cityStatePostalCode = [
				address.city ? `${address.city},` : '',
				address.stateProvince ? address.stateProvince : '',
				address.postalCode ? address.postalCode : '',
			]
				.filter(str => str)
				.join(' ');

			return [
				address.name,
				address.companyName,
				address.addressLine1,
				cityStatePostalCode,
			].filter(str => isString(str) && str !== '');
		}

		return [];
	};

	renderAddress = address => {
		const lines = this.buildAddressLines(address);
		return lines.map((line, i) => (
			<React.Fragment key={i}>
				{line}
				{i < lines.length - 1 && <br />}
			</React.Fragment>
		));
	};

	renderUSPSPrintContent = () => {
		const { selectedPrint, labelDetail } = this.props;
		const { costDetails = [] } = labelDetail?.labelDetails?.shipmentCost ?? {};
		const { printDetails } = selectedPrint;
		const subTotal =
			printDetails?.netstampCount > 0
				? selectedPrint.totalAmount / printDetails?.netstampCount
				: selectedPrint.totalAmount;

		return (
			<div className="history history-print-receipt">
				<div className="header">
					<div className="logo" />
					<div className="content">
						<h3>
							<strong>Receipt</strong>
						</h3>
						<br />
						<p>Print Date: {moment().format('MMM DD, YYYY')}</p>
					</div>
				</div>
				<table>
					<tbody>
						<tr>
							<td colSpan="4" className="print-receipt-section" />
						</tr>
						<tr>
							<td className="title" colSpan="2">
								RETURN TO
							</td>
							<td className="title">REFERENCE</td>
							<td />
						</tr>
						<tr>
							{/* this cell will span the next three rows, so the three four rows each only need two cells */}
							<td colSpan="2" rowSpan="4">
								<div>{this.renderAddress(printDetails.returnAddress)}</div>
							</td>
							<td>Ship Date:</td>
							<td className="align-right">
								{printDetails.shipDate
									? moment(printDetails.shipDate).format('MMM DD, YYYY')
									: ''}
							</td>
						</tr>
						<tr>
							<td>Ship from ZIP: </td>
							<td className="align-right">
								{printDetails.fromAddress.postalCode || ''}
							</td>
						</tr>
						<tr>
							<td>Weight: </td>
							<td className="align-right">{`${
								printDetails.weightPounds || 0
							}lbs. ${printDetails.weightOunces || 0}oz.`}</td>
						</tr>
						<tr>
							<td>User: </td>
							<td className="align-right">{selectedPrint.username}</td>
						</tr>
						<tr>
							<td className="title" colSpan="2">
								SHIP TO
							</td>
							<td>Cost Code:</td>
							<td className="align-right">{printDetails.costCodeName}</td>
						</tr>
						<tr>
							{/* this cell will span the next three rows, so the next three rows each only need two cells */}
							<td colSpan="2" rowSpan="4">
								<div>{this.renderAddress(printDetails.toAddress)}</div>
							</td>
							<td>Refund Type: </td>
							<td className="align-right">{printDetails.refundTypeName}</td>
						</tr>
						<tr>
							<td>Reference #: </td>
							<td className="align-right">{printDetails.memo}</td>
						</tr>
						<tr>
							<td>Printed on: </td>
							<td className="align-right">{printDetails.printTypeName}</td>
						</tr>
						<tr>
							<td>Delivery Status: </td>
							<td className="align-right">{printDetails.printStatusName}</td>
						</tr>
						<tr>
							<td colSpan="2" />
							<td>Tracking #: </td>
							<td className="align-right tracking-number">
								{printDetails.trackingNumber}
							</td>
						</tr>
						{/* only if "carrierAcceptanceDate" is not null */}
						{printDetails.carrierAcceptanceDate ? (
							<tr>
								<td colSpan="2" />
								<td>Carrier Acceptance Date:</td>
								<td className="align-right">
									{moment(printDetails.carrierAcceptanceDate).format(
										'MMM DD, YYYY'
									)}
								</td>
							</tr>
						) : null}
					</tbody>
				</table>
				<br />
				<table>
					<tbody>
						<tr className="print-receipt-section">
							<td className="title" colSpan="3">
								SERVICE
							</td>
							<td className="title align-right" colSpan="2">
								UNIT PRICE
							</td>
						</tr>
						{costDetails.map((costDetail, index) => {
							if (costDetail.feeType === 'service') {
								return (
									<tr key={`service_${index}`}>
										<td colSpan="3">
											{costDetail.feeName}
											{printDetails.packagingTypeName
												? ` ${printDetails.packagingTypeName}`
												: ''}
										</td>
										<td />
										<td className="align-right">
											{usCurrencyFormatter.format(costDetail.cost ?? 0)}
										</td>
									</tr>
								);
							}

							if (costDetail.feeType === 'tracking') {
								return (
									<tr key={`tracking_${index}`}>
										<td colSpan="3">Tracking</td>
										<td />
										<td className="align-right">
											{usCurrencyFormatter.format(costDetail.cost ?? 0)}
										</td>
									</tr>
								);
							}

							if (costDetail.feeType === 'insurance') {
								return (
									<tr key={`insurance_${index}`}>
										<td colSpan="3">
											Insurance ({costDetail.feeName} (
											{usCurrencyFormatter.format(costDetail.cost ?? 0)}))
										</td>
										<td />
										<td className="align-right">
											{usCurrencyFormatter.format(costDetail.cost ?? 0)}
										</td>
									</tr>
								);
							}

							if (
								costDetail.feeType === 'tracking' ||
								costDetail.feeType === 'service' ||
								costDetail.feeType === 'insurance'
							) {
								return;
							}
							return (
								<tr key={index}>
									<td colSpan="3">{costDetail.feeName}</td>
									<td />
									<td className="align-right">
										{usCurrencyFormatter.format(costDetail.cost ?? 0)}
									</td>
								</tr>
							);
						})}
						<tr>
							<td colSpan="5" />
						</tr>
						<tr>
							<td colSpan="3" />
							<td>Subtotal</td>
							<td className="align-right">
								{usCurrencyFormatter.format(subTotal)}
							</td>
						</tr>
						<tr>
							<td colSpan="3" />
							<td>Label Quantity</td>
							<td className="align-right">
								{printDetails?.netstampCount > 0
									? printDetails?.netstampCount
									: 1}
							</td>
						</tr>
						<tr>
							<td colSpan="3" />
							<td>Total Cost</td>
							<td className="align-right">
								{usCurrencyFormatter.format(selectedPrint.totalAmount)}
							</td>
						</tr>
						{printDetails.insuranceCost < 0 && printDetails.insuredValue > 0 ? (
							<tr>
								<td colSpan="5" className="insurance-note">
									* Insurance fees will be processed during your next billing
									cycle.
								</td>
							</tr>
						) : null}
					</tbody>
				</table>
			</div>
		);
	};

	// print content for Fedix
	renderFedixPrintContent = () => {};

	renderPrintReceiptContent = () => {
		const { selectedPrint } = this.props;
		const { printDetails } = selectedPrint;

		if (printDetails.carrier === Carrier.USPS) {
			return this.renderUSPSPrintContent();
		} else if (printDetails.carrier === Carrier.FedEx) {
			return this.renderFedixPrintContent();
		}
	};

	render() {
		return (
			<div className="history-print-receipt">
				<div style={{ display: 'none' }}>
					<div ref={el => (this.pritReceiptRef = el)}>
						{this.renderPrintReceiptContent()}
					</div>
				</div>
				<ReactToPrint
					trigger={() => (
						// NOTE: this button is used to trigger the printing feature in browser.
						// Do NOT pass an `onClick` prop as it will be overwritten.
						<button
							type="button"
							style={{ display: 'none' }}
							ref={this.reactToPrintButton}
						>
							Print
						</button>
					)}
					content={() => this.pritReceiptRef}
				/>
			</div>
		);
	}
}

PrintReceipt.propType = {
	selectedPrint: PropTypes.object.isRequired,
};

export default PrintReceipt;
